import React from 'react'

import {
  AutocompleteSingle,
  AutocompleteSingleProps,
} from 'components/Autocomplete'
import { bigCoins, useAsset, useAssets } from 'contexts/Assets'
import { Asset } from 'types/Asset'
import { isLoadedAndNonNullish } from 'utils/loadable'

/**
 * Substring filtering with sorting biased by market cap, full-word match, and
 * start-of-string match
 */
export function filterItems<A extends Asset>(
  inputValue: string,
  items: A[],
): A[] {
  const scored: [A, number][] = items.map(asset => {
    const tickerLower = asset.ticker.toLowerCase()
    const inputLower = inputValue.toLowerCase()
    let score = 0
    if (inputLower !== '' && tickerLower === inputLower) {
      // make sure that exact matches always sort above big coins
      score = 40
    } else if (
      bigCoins.includes(tickerLower) &&
      tickerLower.startsWith(inputLower)
    ) {
      // ... then prioritise big coins
      score =
        30 - bigCoins.findIndex(coin => coin === tickerLower) / bigCoins.length
    } else if (tickerLower.startsWith(inputLower)) {
      // ... then prioritise matches at the start of the ticker
      score = 20 + inputLower.length / tickerLower.length
    } else if (tickerLower.includes(inputLower)) {
      // ... finally prioritise substring matches
      score = 10 + inputLower.length / tickerLower.length
    }
    return [asset, score]
  })
  return (
    scored
      .filter(([, score]) => score > 0)
      // Assets are pre-sorted by ticker, so matches with the same score will
      // be sorted alphabetically
      .sort(([, aScore], [, bScore]) => bScore - aScore)
      .map(([asset]) => asset)
  )
}

type PropsToOmit =
  | 'items'
  | 'keyBy'
  | 'filterItems'
  | 'formatItem'
  | 'searchKey'

export type AssetAutocompleteProps = Omit<
  AutocompleteSingleProps<Asset>,
  PropsToOmit
> & {
  assetClasses?: Asset['asset_class'][]
  assets?: Asset[]
  filter?: (asset: Asset) => boolean
}

export function AssetAutocomplete({
  assetClasses,
  assets: inputAssets,
  filter,
  ...props
}: AssetAutocompleteProps): JSX.Element {
  const assetsRes = useAssets(inputAssets?.map(a => a.id))
  let assets = assetsRes.loading ? [] : assetsRes.data
  if (assetClasses) {
    assets = assets.filter(({ asset_class }) =>
      assetClasses.includes(asset_class),
    )
  }
  if (filter) {
    assets = assets.filter(filter)
  }

  return (
    <AutocompleteSingle
      items={assets}
      filterItems={filterItems}
      keyBy="id"
      formatItem={item => item.ticker}
      {...props}
      inputProps={{
        ...props.inputProps,
        disabled: assetsRes.loading || props.inputProps?.disabled,
      }}
    />
  )
}

export function AssetAutocompleteId(
  props: Omit<AssetAutocompleteProps, 'selectedItem' | 'onSelect'> & {
    selectedItem: number | undefined
    onSelect: (id: number | undefined) => void
  },
): JSX.Element {
  const asset = useAsset(props.selectedItem)
  return (
    <AssetAutocomplete
      {...props}
      selectedItem={isLoadedAndNonNullish(asset) ? asset.data : null}
      onSelect={asset => props.onSelect(asset?.id)}
      inputProps={{
        ...props.inputProps,
        disabled: !!props.inputProps?.disabled || asset.loading,
      }}
    />
  )
}
